// Path: src/components/aols/privacy/OLSsPrivacy.tsx
// Updated to fix the double footer issue by removing the local Footer instance.
// Removed the redundant FullHeightLayout wrapper for better integration with parent layouts.
// Provided complete file content as requested.
// 06/07/25 10:16 AM +07
import React, { useCallback, Fragment } from "react";
import styled from "styled-components";
import { Typography, Row, Col } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
// REMOVED: Footer import is no longer needed in this component.
// REMOVED: Unused Layout import from 'antd'.
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

// Define props interface
interface OLSsPrivacyProps {}

// Styled components
// REMOVED: FullHeightLayout is no longer needed.

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;
  position: relative; /* Ensure content starts below the breadcrumb */
  margin-top: 40px; /* Add margin to prevent overlap with breadcrumb */

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
    margin-top: 30px; /* Adjust margin for smaller screens */
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

interface StyledLinkProps {
  $isButton?: boolean;
}

const MainTitleButton = styled(Link)<StyledLinkProps>`
  background: #ffaa00; /* Match the button style of other privacy pages */
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const CustomButton = styled.button`
  background: #ffaa00; /* Match the button style of other privacy pages */
  color: #0427f4;
  margin: 10px 0;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover,
  &:focus {
    background: #ffcc00;
    color: #0427f4;
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important; /* White to match dark theme */
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubsectionTitle = styled(Title)`
  color: #ffffff !important; /* White to match dark theme */
  margin-top: 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd; /* Light gray to match dark theme */
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const PolicySection = styled.div`
  margin-bottom: 20px;
`;

const PolicyStep = styled.div`
  margin-bottom: 10px;
`;

const MainTitleText = styled(Title)`
  color: #ffffff !important; /* White to match dark theme */
  margin-top: 20px;
  margin-bottom: 15px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const OLSsPrivacy: React.FC<OLSsPrivacyProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    // UPDATED: Using React.Fragment instead of FullHeightLayout
    <Fragment>
      <Helmet>
        <title>OLS Student Privacy Policy - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Review the OLS Student Privacy Policy for OneLightSystem OLS, detailing the enrollment process, data protection, and terms for OLS Meditation Academy students."
        />
        <meta
          name="keywords"
          content="OLS Student, Privacy Policy, OneLightSystem OLS, Meditation Academy, Enrollment Process, Student Data Protection"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLS Student Privacy Policy - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Explore the OLS Student Privacy Policy for OneLightSystem OLS, covering enrollment, data privacy, and terms for students of the Meditation Academy."
        />
        <meta property="og:url" content="https://www.olsme.com/OLSsPrivacy" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Student Privacy Policy - OneLightSystem OLS" />
        <meta
          name="twitter:description"
          content="Learn about the OLS Student Privacy Policy for OneLightSystem OLS, including enrollment steps and student data protection."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/OLSsPrivacy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLS Student Privacy Policy - OneLightSystem OLS",
            description:
              "OLS Student Privacy Policy for OneLightSystem OLS, detailing the enrollment process, data protection, and terms for OLS Meditation Academy students.",
            url: "https://www.olsme.com/OLSsPrivacy",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave", // Added street address
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002", // Added postal code
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "OLS Student Privacy",
                  item: "https://www.olsme.com/OLSsPrivacy",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/privacy" className="breadcrumb-link">
              Main Privacy
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            OLS Student Privacy
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <Row justify="center" align="middle">
          <Col span={24} style={{ textAlign: "center" }}>
            <LogoImg
              src={logo}
              alt="OLS Logo"
              onClick={handleLogoClick}
            />
            <SectionTitle level={1}>
              OLS Student Privacy Disclosure
            </SectionTitle>
            <MainTitleWrapper>
              <MainTitleButton to="/privacy" aria-label="Go to Privacy page">
                onelightsystem
              </MainTitleButton>
              <MainTitleButton to="/OLSaeo" aria-label="Go to OLS iee.aeo page">
                OLS iee.aeo
              </MainTitleButton>
              <MainTitleButton
                to="/OLSstudentProspect"
                aria-label="Go to OLS Student Prospect page"
              >
                OLS Student Prospect
              </MainTitleButton>
            </MainTitleWrapper>
            <PolicyText>
              ONELIGHTSYSTEM (OLS) LLC
              <br />
              © Private Privacy Disclosure Updates 04/2025
              <br />
              OLS Student (Enrollment Process)
              <br />
              oneLightsystem OLS
              <br />
              2404.OLS iee.aeo
              <br />
              OLS Meditation Academy
            </PolicyText>

            <SectionTitle level={1}>
              1. OLS Enrollment and Services
            </SectionTitle>
            <PolicyText>
              From OLS student to graduate master (OLS aste), the enrollment process includes
              evaluation, application, and tuition service fees for new prospective students and/or
              past reinstatement evaluations.
            </PolicyText>

            <SubsectionTitle level={2}>
              OLS Intro and Initi Process
            </SubsectionTitle>
            <PolicySection>
              <PolicyText>
                OLS INTRO ®© and OLS INITI ®© are exclusively available from official OLS sources.
                Contact{" "}
                <LinkStyled href="mailto:intro@ols-med.net" aria-label="Email intro@ols-med.net">
                  intro@ols-med.net
                </LinkStyled>{" "}
                for access. The process includes the following steps:
              </PolicyText>

              <PolicyStep>
                <PolicyText>
                  <strong>1: Application Submission</strong> Submit an application to receive an
                  evaluated service value for OLS Intro and Initi.
                </PolicyText>
              </PolicyStep>

              <PolicyStep>
                <PolicyText>
                  <strong>2: Prospect Evaluation</strong> Receive your evaluated service value from{" "}
                  <LinkStyled href="mailto:intro@ols-med.net" aria-label="Email intro@ols-med.net">
                    intro@ols-med.net
                  </LinkStyled>.
                </PolicyText>
              </PolicyStep>

              <PolicyStep>
                <PolicyText>
                  <strong>3: OLS Intro 50% Payment</strong> Make a non-refundable 50% payment to
                  schedule your OLS Intro session.
                </PolicyText>
              </PolicyStep>

              <PolicyStep>
                <PolicyText>
                  <strong>4: Schedule OLS Intro</strong> Arrange the introductory session with your
                  assigned OLS inuo or eae.
                </PolicyText>
              </PolicyStep>

              <PolicyStep>
                <PolicyText>
                  <strong>5: OLS Initi 50% Payment</strong> After completing OLS Intro, make a second
                  non-refundable 50% payment under the Non-Disclosure Agreement (NDA) to schedule OLS
                  Initi.
                </PolicyText>
              </PolicyStep>

              <PolicyStep>
                <PolicyText>
                  <strong>6: Schedule OLS Initi</strong> Finalize the initiation session, which may
                  include a follow-up call to address any final questions or confirm details.
                </PolicyText>
              </PolicyStep>

              <SubsectionTitle level={2}>
                Non-Response Periods:
              </SubsectionTitle>

              <PolicyStep>
                <PolicyText>
                  <strong>9-Day Period:</strong> If the initial 50% payment for OLS Intro is not made
                  within 9 days of evaluation, you will receive a notice. Failure to pay may result
                  in the application expiring.
                </PolicyText>
              </PolicyStep>

              <PolicyStep>
                <PolicyText>
                  <strong>30-Day Period:</strong> If the final 50% payment for OLS Initi is not made
                  within 30 days after OLS Intro, the application will expire. To reapply, you will
                  incur an additional 10% fee on the original evaluated service value for
                  re-evaluation.
                </PolicyText>
              </PolicyStep>

              <PolicyText>
                All payments are non-refundable, and timely action is required to maintain your
                application status.
              </PolicyText>
            </PolicySection>

            <SubsectionTitle level={2}>
              Privacy Guarantee
            </SubsectionTitle>
            <PolicyText>
              oneLightsystem OLS CEO | CTO guarantees not to share personal prospect information
              with any third parties (OLS data privacy protection).
            </PolicyText>

            <SectionTitle level={1}>
              2. Evaluation and Fees
            </SectionTitle>
            <PolicyText>
              <strong>Evaluation Service Fees (2025):</strong> New evaluations are free for 2025.
              Fees are calculated based on age, location (GDP), and experience. Each re-evaluation
              after failing to proceed within 10 days increases by 10% of the original value. After
              30 days, a new evaluation is required.
            </PolicyText>

            <SectionTitle level={2}>
              Payment Structure
            </SectionTitle>
            <SubsectionTitle level={2}>
              OLS Intro & OLS Initi Payments:
            </SubsectionTitle>
            <PolicyText>
              After evaluation, a non-refundable 50% payment is required to schedule OLS Intro, with
              the remaining 50% due post-Intro for OLS Initi. Payments may be credited for future
              applications if not completed.
              <br />- <strong>Passive Fees:</strong> Calculated as the total OLSM (meditation
              minutes) by your dedicated OLS inuo or OLS eae, multiplied by passive days, in local
              currency.
              <br />- <strong>Past-Initiated Fees:</strong> Requires re-evaluation of OLS Intro &
              Initi service fees after 30 days of passivity.
            </PolicyText>

            <SectionTitle level={1}>
              3. Support and Opportunities
            </SectionTitle>
            <PolicyText>
              Each OLS student receives personal support from a selected OLS inuo (instructor) or
              OLS eae (teacher). Support includes:
              <br />- Live OLSLM meditation video access.
              <br />- Call or text support, free if you’re the only student in your area or based on
              OLS data center standings. Priority requests may be purchased if queued.
              <br />- Support hours are daytime only, determined by your OLS inuo or eae.
            </PolicyText>

            <SectionTitle level={1}>
              4. Non-Disclosure Agreement (NDA)
            </SectionTitle>
            <PolicyText>
              Before OLS Initi, students must review and agree to the NDA. Sharing visual practice
              (not teachings) via pictures is allowed, but describing teachings (via voice, video,
              or verbally) is prohibited until becoming an OLS inuo. Violations may result in bans,
              penalties (equal to OLS Intro value), or temporary removal, as determined by OLS
              iee.aeo.
            </PolicyText>
            <PolicyText>
              OLS students can refer others to{" "}
              <LinkStyled href="https://www.ols-med.net" aria-label="Visit ols-med.net">
                www.ols-med.net
              </LinkStyled>{" "}
              for enrollment but cannot provide teachings themselves until officially authorized.
            </PolicyText>

            <SectionTitle level={1}>
              5. Student Activity and Status
            </SectionTitle>
            <PolicyText>
              <strong>Statuses:</strong> Active, Passive, Past Initiated, Canceled, or Deleted.
              <br />- <strong>Passive:</strong> After 9 days of no activity (no OLSLM or OLSM
              entry). Reactivation within 30 days incurs passive fees.
              <br />- <strong>Past Initiated:</strong> After 30 days of passivity, requiring
              re-evaluation of Intro & Initi fees.
              <br />- <strong>Cancellation/Deactivation:</strong> Students can cancel or deactivate
              anytime, transitioning to Past Initiated or OLSME status. Data remains archived in
              the OLS data center post-deletion notice.
            </PolicyText>

            <SubsectionTitle level={2}>
              Activity Requirements
            </SubsectionTitle>
            <PolicyText>
              - <strong>Active Status:</strong> Perform 1-9 OLSLM or OLSM within 9 days, or 1-30
              OLSM within 30 days for OLS eae. OLSME activity doesn’t count for new students.
              <br />- <strong>Safe Hours:</strong> OLS is not responsible for student well-being if
              OLSLM is received outside safe hours, though it logs as activity.
            </PolicyText>

            <SectionTitle level={1}>
              6. Safety and Responsibility
            </SectionTitle>
            <PolicyText>
              <strong>OLS Main Rule: Safety First.</strong> Direct Light (sun energy) is powerful.
              Follow recommendations from your OLS inuo or eae, even during safe hours. Users are
              responsible for determining their light exposure.
              <br />
              This information is for educational purposes only and should not substitute for
              medical advice. Consult your physician or healthcare professional.
            </PolicyText>
            <PolicyText>
              OLS is not liable for any harm caused, even during safe hours. Submit OLSLM (Light
              Minutes) to the OLS data center with proof (pictures/videos) and a truthfulness
              statement post-Initi.
            </PolicyText>

            <SectionTitle level={1}>
              7. Opportunities and Upgrades
            </SectionTitle>
            <PolicyText>
              OLS students can qualify for roles like OLS inuo or OLS eae after meeting activity
              and fee requirements (e.g., 60 days, 60 OLSLM, or 10 OLSM). Fees vary by location GDP.
            </PolicyText>

            <CustomButton>
              <Link to="/olssregister" aria-label="Register as OLS Student">
                Register as OLS Student
              </Link>
            </CustomButton>

            <SectionTitle level={1}>
              8. Legal Notices
            </SectionTitle>
            <PolicyText>
              The information here is confidential and protected from disclosure. Unauthorized
              dissemination is prohibited. All OLS teachings and practices are copyrighted by
              oneLightsystem OLS, LLC.
            </PolicyText>

            <Text>Reference: 805.2404.OLS iee.aeo. OLS aste. eae Asvitloaten</Text>
          </Col>
        </Row>
      </ContentWrapper>
      {/* REMOVED: The <Footer /> component is no longer rendered here. */}
    </Fragment>
  );
};

export default OLSsPrivacy;